import { useRef, useState, useEffect } from 'react';
import { BroadcastChannel, createLeaderElection } from 'broadcast-channel';
import { v4 as uuid } from 'uuid';

import { MessageOwner, ThreadMessages } from 'types';
import NotificationSound from 'assets/audios/notification-sound.mp3';
import { isSafari } from 'utils/detectBrowser';
import { NOTIFICATIONS_INTERVAL_TIME } from 'constants/chat';
import { STORAGE_KEY } from 'constants/browserStorageKeys';
import {
  getStorageItem,
  setStorageItem,
  withCrossTabSync,
} from 'services/storage';
import { getSessionStorage, setSessionStorage } from 'services/sessionStorage';
import { useLazyEffect } from './useLazyEffect';
import { useTranslation } from '@omilia/providers';

interface UseNotifications {
  withTitle: boolean;
  withSound: boolean;
  isMinimized: boolean;
  isOpen: boolean;
  messages: ThreadMessages[];
  audioSrc: string;
}

export const useNotifications = ({
  withTitle,
  withSound,
  isMinimized,
  isOpen,
  messages,
  audioSrc,
}: UseNotifications) => {
  const { t } = useTranslation();
  const interval = useRef(null);
  const sound = useRef(null);
  const defaultTitle = useRef(document.title);
  const [isLeaderTab, setLeaderTab] = useState(false);
  const [shouldCount, setCountMessages] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(
    getStorageItem(STORAGE_KEY.UNREAD_MESSAGES),
  );

  const getBotMessages = (messages: ThreadMessages[] = []) =>
    messages.filter(p => p.message && p.owner !== MessageOwner.USER);

  const statusChannel = new BroadcastChannel('status');
  const messageChannel = new BroadcastChannel('unreadMessages');

  /*
   * Set Leader Tab randomly. Leader tab calculates unread messages and plays sound.
   */
  const elector = createLeaderElection(statusChannel);
  elector.awaitLeadership().then(() => {
    setLeaderTab(true);
  });

  /*
   * When chats are not connected through the tabs, then each tab should be a LeaderTab
   */
  useEffect(() => {
    if (!withCrossTabSync) {
      setLeaderTab(true);
    }
  }, [withCrossTabSync]);

  messageChannel.onmessage = event => {
    setUnreadMessages(event.unreadMessages);
  };

  /*
   * Each tab has isVisible boolean param that tells if chat messages are visible for the user
   * Using this param we decide if the unread messages exists.
   */
  statusChannel.onmessage = event => {
    const chatsStatus = getSessionStorage(STORAGE_KEY.CHATS_STATUS);
    const currentChatID = getSessionStorage(STORAGE_KEY.CURRENT_CHAT_STATUS)
      ?.chatID;

    if (event.chatID === currentChatID) {
      setCurrentChatStatus(currentChatID, event.isVisible);
    }

    if (isLeaderTab) {
      updateAllChatsStatus(chatsStatus, event.chatID, event.isVisible);
    }
  };

  const handleVisibilityChange = () => {
    const isVisible = !document.hidden && !isMinimized && isOpen;
    const chatsStatus = getSessionStorage(STORAGE_KEY.CHATS_STATUS);
    const currentChatID = getSessionStorage(STORAGE_KEY.CURRENT_CHAT_STATUS)
      ?.chatID;

    setCurrentChatStatus(currentChatID, isVisible);
    if (isLeaderTab) {
      updateAllChatsStatus(chatsStatus, currentChatID, isVisible);
    }

    if (isVisible) {
      resetTitle();
      setLastSeenMessage();
      setUnreadMessagesNumber(0);
      // Safari Bug: it plays sound second time when user opens previously inactive tab.
      sound.current.volume = 0;
    }
  };

  const setCurrentChatStatus = (chatID: string, isVisible: boolean) => {
    setSessionStorage(STORAGE_KEY.CURRENT_CHAT_STATUS, {
      chatID,
      isVisible,
    });
  };

  const setUnreadMessagesNumber = (value: number) => {
    if (withCrossTabSync) {
      messageChannel.postMessage({ unreadMessages: value });
    } else {
      setUnreadMessages(value);
    }
    setStorageItem(STORAGE_KEY.UNREAD_MESSAGES, value);
  };

  const setLastSeenMessage = () => {
    const botMessages = getBotMessages(messages);

    if (botMessages.length) {
      setStorageItem(
        STORAGE_KEY.LAST_SEEN_MESSAGE_ID,
        botMessages[botMessages.length - 1].sequence_id,
      );
    }
  };

  const updateAllChatsStatus = (chats, tabID, status) => {
    const updatedChats = status
      ? Object.keys(chats).reduce((acc, curr) => {
          return {
            ...acc,
            [curr]: false,
          };
        }, {})
      : chats;

    updatedChats[tabID] = status;
    setSessionStorage(STORAGE_KEY.CHATS_STATUS, updatedChats);

    setCountMessages(!Object.values(updatedChats).some(tab => tab));
  };

  const resetTitle = () => {
    document.title = defaultTitle.current;
    clearInterval(interval.current);
  };

  const playSilentSound = () => {
    if (isSafari) {
      sound.current.volume = 0;
      sound.current.play();
    }
  };

  /*
   * Update chat visibility field when receiving a new message
   */
  useEffect(() => {
    statusChannel.postMessage({
      chatID: getSessionStorage(STORAGE_KEY.CURRENT_CHAT_STATUS)?.chatID,
      isVisible: !document.hidden && !isMinimized && isOpen,
    });
  }, [messages.length]);

  /*
   * Set tab ID
   */
  useEffect(() => {
    const currentChatID = getSessionStorage(STORAGE_KEY.CURRENT_CHAT_STATUS)
      ?.chatID;
    const chatID = currentChatID || uuid();

    if (!currentChatID) {
      setCurrentChatStatus(chatID, false);
    }
    if (isLeaderTab) {
      setSessionStorage(STORAGE_KEY.CHATS_STATUS, {
        [chatID]: false,
      });
    }
  }, [isLeaderTab]);

  /*
   * Mark all unread messages as read on chat open.
   */
  useEffect(() => {
    if (!isMinimized && isOpen) {
      setUnreadMessagesNumber(0);
    }
  }, [isOpen, isMinimized]);

  /*
   * Count number of unread messages
   * Counting by a tab is determined by 2 conditions:
   * 1) if upon all opened tabs there is none which is visible to user
   * 2) tab should be a leader (determined by leader-election algorithm)
   */
  useLazyEffect(() => {
    if (shouldCount && isLeaderTab) {
      const messageNum = getBotMessages(messages).filter(
        msg =>
          getStorageItem(STORAGE_KEY.LAST_SEEN_MESSAGE_ID) < msg.sequence_id,
      );

      setUnreadMessagesNumber(messageNum.length);
    }
  }, [messages, shouldCount, isLeaderTab]);

  /*
   * Set last message sequence_id as LAST_SEEN_MESSAGE_ID if dialog was visible to the user
   */
  useEffect(() => {
    if (!isMinimized && isOpen && !document.hidden) {
      setLastSeenMessage();
    }
  }, [isMinimized, isOpen, messages]);

  /*
   * Download notification sounds
   */
  useEffect(() => {
    sound.current = new Audio(audioSrc || NotificationSound);
  }, [audioSrc]);

  /*
   * Play sound notifications
   */
  useEffect(() => {
    if (
      !getStorageItem(STORAGE_KEY.MUTE_SOUND) &&
      withSound &&
      unreadMessages &&
      isLeaderTab
    ) {
      sound.current.volume = 1;
      if (isSafari) {
        sound.current.load();
      }
      sound.current.play();
    }
  }, [withSound, unreadMessages, audioSrc]);

  /*
   * Show notifications in the document.title
   */
  useEffect(() => {
    if (withTitle && unreadMessages) {
      interval.current = setInterval(() => {
        if (document.title === defaultTitle.current) {
          document.title = t('document.newMessages', {
            count: unreadMessages,
          });
        } else {
          document.title = defaultTitle.current;
        }
      }, NOTIFICATIONS_INTERVAL_TIME);
    }

    return () => {
      resetTitle();
    };
  }, [withTitle, unreadMessages]);

  /*
   * Listen to document.hidden property change
   */
  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isOpen, isMinimized, isLeaderTab, messages]);

  return {
    unreadMessages,
    playSilentSound,
  };
};
